import baseUrl from "../vars";

// Авторизация для нескольких вкладок: refresh из localStorage, синхронизация jwt между вкладками

const CHANNEL_NAME = "dashboard-auth";
const LOCK_NAME = "dashboard-auth-refresh";
const ACCESS_TOKEN_KEY = "access_token"; // jwt на текущую вкладку
const REFRESH_IN_PROGRESS_KEY = "refresh_in_progress";
const REFRESH_LOCK_TS_KEY = "refresh_lock_ts";
// Предел ожидания замка обновления входа. Ограничение обязательное:
// без него зависшая вкладка запирает все остальные навсегда.
// Подробности — у requestRefreshLock ниже.
const LOCK_TIMEOUT_MS = 30000;

let pageStoreRef = null;
let broadcastChannel = null;
let inFlightRefreshPromise = null; // дедуп параллельных refresh
let listenersInitialized = false;

export function registerPageStore(store) {
  // loader роутера вызывается раньше React Provider
  pageStoreRef = store;
}

function getPageStore() {
  return pageStoreRef;
}

function isAccessTokenValid(jwt) {
  if (!jwt) return false;
  try {
    const payload = JSON.parse(
      atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    if (!payload.exp) return false;
    return payload.exp * 1000 > Date.now() + 30_000;
  } catch {
    return false;
  }
}

function handleAuthMessage(data) {
  const pageStore = getPageStore();
  if (!pageStore || !data?.type) return;

  if (data.type === "token" && data.jwt_token) {
    applyToken(data.jwt_token, { broadcast: false });
    return;
  }

  if (data.type === "logout") {
    applyLogoutLocal({ broadcast: false });
  }
}

function setupBroadcastChannel() {
  // синхронизация jwt и logout между вкладками
  if (typeof BroadcastChannel === "undefined" || broadcastChannel) return;

  broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
  broadcastChannel.onmessage = (event) => handleAuthMessage(event.data);
}

function setupStorageListener() {
  // запасной вариант, если нет BroadcastChannel
  if (listenersInitialized) return;
  listenersInitialized = true;

  window.addEventListener("storage", (event) => {
    const pageStore = getPageStore();
    if (event.key === ACCESS_TOKEN_KEY && event.newValue && pageStore) {
      pageStore.updateToken(event.newValue);
      return;
    }

    if (event.key === "auth_logout" && pageStore) {
      applyLogoutLocal({ broadcast: false });
    }
  });
}

function applyToken(jwt_token, { broadcast = true } = {}) {
  if (!jwt_token) return;

  // jwt в sessionStorage и MobX; refresh_token в localStorage не трогаем
  sessionStorage.setItem(ACCESS_TOKEN_KEY, jwt_token);
  getPageStore()?.updateToken(jwt_token);

  if (broadcast) {
    broadcastChannel?.postMessage({ type: "token", jwt_token });
    try {
      // триггер storage-события в других вкладках
      localStorage.setItem(ACCESS_TOKEN_KEY, jwt_token);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch {
      // ок, останется BroadcastChannel
    }
  }
}

function applyLogoutLocal({ broadcast = true } = {}) {
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("email");
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);

  const pageStore = getPageStore();
  if (pageStore) {
    pageStore.updateToken("");
    pageStore.updateEmail("");
  }

  if (broadcast) {
    broadcastChannel?.postMessage({ type: "logout" });
    try {
      localStorage.setItem("auth_logout", String(Date.now()));
      localStorage.removeItem("auth_logout");
    } catch {
      // ignore
    }
  }
}

async function waitForAccessToken(maxWaitMs = 15000) {
  // ждем пока соседняя вкладка закончит refresh
  const started = Date.now();

  while (Date.now() - started < maxWaitMs) {
    const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (token && isAccessTokenValid(token)) {
      getPageStore()?.updateToken(token);
      return token;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return null;
}

/**
 * ВАЖНО: ожидание замка обязано быть ограничено по времени. Не убирать.
 *
 * Замок нужен, чтобы вкладки одного человека не дёргали /api/auth/refresh
 * одновременно. Но `navigator.locks.request` без сигнала ждёт освобождения
 * БЕСКОНЕЧНО. Если вкладка, взявшая замок, зависла на запросе или её усыпил
 * браузер, все остальные вкладки вставали навсегда — человек видел не ошибку,
 * а вечную загрузку, и не мог войти. Ровно это ловили у пользователя по
 * удалённому рабочему столу: «не пускает по 5–10 минут, помогает только
 * перезаход».
 *
 * Поэтому ждём не дольше LOCK_TIMEOUT_MS, а потом обновляемся без замка.
 * Это безопасно: сервер при обновлении не меняет refresh-токен (см.
 * AuthService.refresh в dashboard-backend), поэтому два параллельных
 * обновления просто выдадут по access-токену и ничего не сломают.
 * Лишний запрос несравнимо лучше намертво заклинившей вкладки.
 */
async function requestRefreshLock(fn) {
  const controller = new AbortController();
  const giveUp = setTimeout(() => controller.abort(), LOCK_TIMEOUT_MS);

  try {
    return await navigator.locks.request(LOCK_NAME, { signal: controller.signal }, fn);
  } catch (error) {
    // Прерван по таймауту — значит замок держит кто-то зависший.
    if (error?.name !== "AbortError") throw error;
    return await fn();
  } finally {
    clearTimeout(giveUp);
  }
}

async function withRefreshLock(fn) {
  // одна вкладка на /api/auth/refresh за раз
  if (navigator.locks?.request) {
    return requestRefreshLock(fn);
  }

  const now = Date.now();
  const lockTs = Number(sessionStorage.getItem(REFRESH_LOCK_TS_KEY) || 0);

  if (
    sessionStorage.getItem(REFRESH_IN_PROGRESS_KEY) === "1" &&
    now - lockTs < LOCK_TIMEOUT_MS
  ) {
    const token = await waitForAccessToken();
    return token ? { jwt_token: token } : null;
  }

  sessionStorage.setItem(REFRESH_IN_PROGRESS_KEY, "1");
  sessionStorage.setItem(REFRESH_LOCK_TS_KEY, String(now));

  try {
    return await fn();
  } finally {
    sessionStorage.removeItem(REFRESH_IN_PROGRESS_KEY);
  }
}

function syncStoreFromSession() {
  const pageStore = getPageStore();
  if (!pageStore) return;

  const cachedAccess = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (cachedAccess && isAccessTokenValid(cachedAccess) && !pageStore.token) {
    pageStore.updateToken(cachedAccess);
  }

  const email = getStoredEmail();
  if (email) {
    pageStore.updateEmail(email);
  }
}

export function initAuth() {
  setupBroadcastChannel();
  setupStorageListener();
  syncStoreFromSession();
}

export function getStoredRefreshToken() {
  return localStorage.getItem("refresh_token");
}

export function getStoredEmail() {
  return localStorage.getItem("email");
}

function getStoredAccessToken() {
  const pageStore = getPageStore();
  if (pageStore?.token && isAccessTokenValid(pageStore.token)) {
    return pageStore.token;
  }

  const cached = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (cached && isAccessTokenValid(cached)) {
    pageStore?.updateToken(cached);
    return cached;
  }

  return null;
}

export async function refreshAccessToken() {
  if (!getStoredRefreshToken()) return null;

  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  inFlightRefreshPromise = withRefreshLock(async () => {
    const pageStore = getPageStore();
    const cached = sessionStorage.getItem(ACCESS_TOKEN_KEY);

    if (cached && isAccessTokenValid(cached) && (!pageStore || !pageStore.token || pageStore.token === cached)) {
      pageStore?.updateToken(cached);
      return { jwt_token: cached };
    }

    const refresh = getStoredRefreshToken();
    if (!refresh) return null;

    try {
      const response = await fetch(
        `${baseUrl}/api/auth/refresh?rt=${encodeURIComponent(refresh)}`,
        {
          method: "POST",
          headers: { accept: "application/json" },
        }
      );

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      if (!result?.jwt_token) {
        return null;
      }

      applyToken(result.jwt_token);

      const email = getStoredEmail();
      if (email) {
        getPageStore()?.updateEmail(email);
      }

      return { jwt_token: result.jwt_token };
    } catch (error) {
      console.error("Ошибка при обновлении access-токена:", error);
      return null;
    }
  }).finally(() => {
    inFlightRefreshPromise = null;
  });

  return inFlightRefreshPromise;
}

/**
 * Разбирает отказ по слепку и превращает его в человеческую ошибку.
 *
 * База слепков одна на всех, и во время восстановления схема в ней удалена.
 * Раньше отчёт в этот момент получал ответ без поля `data` и молча рисовал
 * пустую таблицу — нули было не отличить от настоящих данных. Теперь запрос
 * падает с текстом, который отчёты уже показывают в своих тостах.
 */
async function backupRefusal(response) {
  if (response.status !== 409) return null;
  try {
    const body = await response.clone().json();
    const detail = body?.detail;
    if (!detail?.code?.startsWith("backup")) return null;
    getPageStore()?.applyBackupStatus?.(detail);
    return new Error(detail.message || "Слепок недоступен");
  } catch {
    return null;
  }
}

export async function authFetch(url, options = {}) {
  const withAuth = async () => {
    const token = getStoredAccessToken() || (await refreshAccessToken())?.jwt_token;
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    // Какой именно слепок ждёт эта страница. Сервер сверит с тем, что развёрнут:
    // коллега мог развернуть другую дату в той же общей базе.
    if (headers["X-Database"] === "temp") {
      const wanted = getPageStore()?.select_backup;
      if (wanted) headers["X-Backup-Date"] = wanted;
    }
    return { ...options, headers };
  };

  let response = await fetch(url, await withAuth());

  // Access мог протухнуть между проверкой и запросом: обновляем один раз.
  if (response.status === 401 && getStoredRefreshToken()) {
    const refreshed = await refreshAccessToken();
    if (refreshed?.jwt_token) {
      response = await fetch(url, await withAuth());
    }
  }

  if (response.status === 401) {
    clearAuthSession();
  }

  const refusal = await backupRefusal(response);
  if (refusal) throw refusal;

  return response;
}

export async function ensureAuthenticated() {
  // память -> sessionStorage -> refresh из localStorage
  const pageStore = getPageStore();

  if (pageStore?.token && isAccessTokenValid(pageStore.token)) {
    return true;
  }

  const cached = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  if (cached && isAccessTokenValid(cached)) {
    pageStore?.updateToken(cached);
    const email = getStoredEmail();
    if (email) {
      pageStore?.updateEmail(email);
    }
    return true;
  }

  if (!getStoredRefreshToken()) {
    return false;
  }

  const result = await refreshAccessToken();
  return Boolean(result?.jwt_token);
}

export function saveLoginSession({ jwt_token, refresh_token, email, remember = true }) {
  applyToken(jwt_token);
  getPageStore()?.updateEmail(email || "");

  // remember = галочка "Запомнить пароль"
  if (remember) {
    if (refresh_token) {
      localStorage.setItem("refresh_token", refresh_token);
    }
    if (email) {
      localStorage.setItem("email", email);
    }
    return;
  }

  localStorage.removeItem("refresh_token");
  localStorage.removeItem("email");
}

export function clearAuthSession({ broadcast = true } = {}) {
  applyLogoutLocal({ broadcast });
}

export async function logoutSession() {
  const refresh = getStoredRefreshToken();
  applyLogoutLocal({ broadcast: true });

  if (refresh) {
    try {
      await fetch(`${baseUrl}/api/auth/logout?rt=${encodeURIComponent(refresh)}`, {
        method: "POST",
        headers: { accept: "application/json" },
      });
    } catch (error) {
      console.error("Ошибка при выходе из аккаунта:", error);
    }
  }
}
